import { Raycaster, Vector2, Vector3 } from 'three';

/**
 * Desire, and the moment it becomes memory.
 *
 * Attention is a ray out of the middle of the frame, nudged a little by the
 * pointer. Whatever it rests on accumulates dwell; when dwell fills, the thing
 * is *remembered* — it gives up a mote to the wake and will never be new
 * again. Look away and dwell drains, but slower than it filled, because that
 * is how wanting works.
 *
 * Two kinds of candidate:
 *   mode 'mesh'   — a handful of real objects, tested with a Raycaster.
 *   mode 'cloud'  — thousands of instance positions, tested analytically
 *                   against the ray, which is far cheaper than per-instance
 *                   geometry intersection.
 */
export class Gaze {
  constructor(ctx) {
    this.ctx = ctx;
    this.cfg = ctx.settings.desire;
    this.raycaster = new Raycaster();
    this.raycaster.far = 120;
    this.ndc = new Vector2();

    this.origin = new Vector3();
    this.direction = new Vector3();

    /** @type {Array<object>} descriptors contributed by layers */
    this.providers = [];

    /** @type {Map<string, number>} key → dwell in seconds */
    this.dwell = new Map();
    /** @type {Set<string>} keys already turned into memory */
    this.remembered = new Set();

    this.current = null; // {key, kind, position, provider, data}
    this.progress = 0;
    this._meshTick = 0;
  }

  register(descriptor) {
    this.providers.push(descriptor);
  }

  clear() {
    this.providers.length = 0;
  }

  isRemembered(key) {
    return this.remembered.has(key);
  }

  dwellOf(key) {
    return this.dwell.get(key) ?? 0;
  }

  /** @param {number} dt @param {import('../core/App.js').FrameState} state */
  update(dt, state) {
    const { camera } = this.ctx;
    const pointer = state.pointer;

    // Attention sits at the centre of the frame but leans toward the pointer.
    this.ndc.set(pointer.x * 0.55, pointer.y * 0.55);
    this.raycaster.setFromCamera(this.ndc, camera);
    this.origin.copy(this.raycaster.ray.origin);
    this.direction.copy(this.raycaster.ray.direction);

    const best = this._pick();

    // Dwell on the thing under attention; everything else forgets.
    for (const [key, v] of this.dwell) {
      if (best && key === best.key) continue;
      const next = v - dt * this.cfg.decay;
      if (next <= 0.001) this.dwell.delete(key);
      else this.dwell.set(key, next);
    }

    if (best) {
      if (!this.remembered.has(best.key)) {
        const next = (this.dwell.get(best.key) ?? 0) + dt;
        this.dwell.set(best.key, next);
        this.progress = Math.min(1, next / this.cfg.dwellToRemember);
        if (next >= this.cfg.dwellToRemember) {
          this.remembered.add(best.key);
          this.dwell.delete(best.key);
          this.progress = 0;
          this.ctx.bus.emit('remember', {
            key: best.key,
            kind: best.kind,
            position: best.position.clone(),
            data: best.data,
          });
        }
      } else {
        this.progress = 1;
      }
      if (!this.current || this.current.key !== best.key) {
        this.ctx.bus.emit('gaze', best);
      }
      this.current = best;
    } else {
      if (this.current) this.ctx.bus.emit('gaze:lost', {});
      this.current = null;
      this.progress = 0;
    }

    return this.current;
  }

  _pick() {
    let best = null;
    let bestScore = Infinity;

    this._meshTick = (this._meshTick + 1) % 2;

    for (const p of this.providers) {
      if (p.enabled === false) continue;
      if (p.mode === 'cloud') {
        const hit = this._pickCloud(p);
        if (hit && hit.score < bestScore) {
          bestScore = hit.score;
          best = hit;
        }
      } else if (this._meshTick === 0 || this.current?.provider === p) {
        const hit = this._pickMesh(p);
        if (hit && hit.score < bestScore) {
          bestScore = hit.score;
          best = hit;
        }
      }
    }
    return best;
  }

  _pickCloud(p) {
    const cloud = p.cloud();
    if (!cloud || !cloud.count) return null;
    const { positions, radii, count } = cloud;
    const maxDist = p.maxDistance ?? 28;
    const base = p.tolerance ?? 0.03;

    const ox = this.origin.x;
    const oy = this.origin.y;
    const oz = this.origin.z;
    const dx = this.direction.x;
    const dy = this.direction.y;
    const dz = this.direction.z;

    let bestIndex = -1;
    let bestScore = Infinity;

    for (let i = 0; i < count; i++) {
      const i3 = i * 3;
      const vx = positions[i3] - ox;
      const vy = positions[i3 + 1] - oy;
      const vz = positions[i3 + 2] - oz;
      const along = vx * dx + vy * dy + vz * dz;
      if (along < 1.2 || along > maxDist) continue;
      const perpSq = vx * vx + vy * vy + vz * vz - along * along;
      if (perpSq < 0) continue;
      const r = radii ? radii[i] : 0.3;
      // Attention is not a cursor. The allowance grows with distance so a
      // shell across the well is as easy to rest on as one at your hand.
      const allowed = r * 3.0 + along * base;
      if (perpSq > allowed * allowed) continue;
      const score = Math.sqrt(perpSq) / Math.max(allowed, 1e-4) + along * 0.012;
      if (score < bestScore) {
        bestScore = score;
        bestIndex = i;
      }
    }

    if (bestIndex < 0) return null;
    const i3 = bestIndex * 3;
    return {
      key: `${p.kind}:${bestIndex}`,
      kind: p.kind,
      index: bestIndex,
      position: new Vector3(positions[i3], positions[i3 + 1], positions[i3 + 2]),
      score: bestScore,
      provider: p,
      data: p.resolve ? p.resolve(bestIndex) : null,
    };
  }

  _pickMesh(p) {
    const objects = p.objects();
    if (!objects || !objects.length) return null;
    this.raycaster.far = p.maxDistance ?? 90;
    const hits = this.raycaster.intersectObjects(objects, true);
    this.raycaster.far = 120;
    if (!hits.length) return null;
    const hit = hits[0];
    const owner = p.owner ? p.owner(hit.object) : hit.object;
    if (!owner) return null;
    return {
      key: `${p.kind}:${owner.uuid}`,
      kind: p.kind,
      object: owner,
      position: owner.getWorldPosition(new Vector3()),
      distance: hit.distance,
      score: hit.distance * 0.004,
      provider: p,
      data: p.resolve ? p.resolve(owner) : null,
    };
  }
}
